import { CustomerRoutes, PublicRoutes, StaffRoutes, TyperRoutes } from "appRoutePaths";
import { Roles, useAuthContext, useIdentity } from "context/auth";
import { useLocation } from "react-router-dom";

interface RedirectionResult {
    shouldRedirect: boolean;
    destination?: string;
    roles: Roles[];
}

export const getUserDashboardUrl = (roles: Roles[]) => {
    if (
        roles.includes(Roles.Admin) ||
        roles.includes(Roles.SeniorStaff) ||
        roles.includes(Roles.Staff)
    ) {
        return StaffRoutes.dashboard.url;
    } else if (
        roles.includes(Roles.Typer)
    ) {
        return TyperRoutes.dashboard.url;
    } else if (
        roles.includes(Roles.User) ||
        roles.includes(Roles.Breaker)
    ) {
        return CustomerRoutes.dashboard.url;
    }

    return PublicRoutes.rootPath;
}

const useIdentityRedirection = (expectedRoles: Roles[]): RedirectionResult => {
    const { isLoggedIn, roles, twoFactorTokenRequired, twoFactorSetupRequired, isInRole, isCustomer, isAdmin } = useIdentity();
    const { isImpersonating } = useAuthContext();
    const location = useLocation();

    const redirectTo = (destination: string) => ({
        shouldRedirect: true,
        destination,
        roles
    });

    const hasExpectedRole = isInRole(expectedRoles);

    if (!isLoggedIn) {
        return redirectTo(PublicRoutes.login.url);

    } else if (isImpersonating()) {
        return { shouldRedirect: false, roles };

    } else if (isAdmin && twoFactorSetupRequired && location.pathname !== StaffRoutes.mandatoryMFA.url) {
        return redirectTo(StaffRoutes.mandatoryMFA.url);

    } else if (isCustomer && twoFactorSetupRequired && location.pathname !== CustomerRoutes.mandatoryMFA.url) {
        return redirectTo(CustomerRoutes.mandatoryMFA.url);

    } else if (twoFactorTokenRequired) {
        return redirectTo(PublicRoutes.twoFactor.url);

    } else if (hasExpectedRole) {
        return { shouldRedirect: false, roles };
    }

    const redirectUrl = getUserDashboardUrl(roles);
    return redirectTo(redirectUrl);
};

export default useIdentityRedirection;